// Numerical symmetry-group computation for a convex polyhedron on the unit sphere.
// For all templates we ship (cube, octahedron, icosahedron, dodecahedron,
// truncated icosahedron), every vertex belongs to a single orbit, so we only
// need one rotation per vertex to map the canonical vertex 0 onto it.

const EPS = 1e-4;

const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const norm3 = (v) => {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};

function frame(u, w) {
  // Build an orthonormal 3x3 matrix (as flat 9-array, column-major)
  // where column 0 = u/|u|, column 1 ⟂ u aligned with w, column 2 = col0 × col1.
  const e1 = norm3(u);
  const wp = sub(w, [e1[0] * dot(w, e1), e1[1] * dot(w, e1), e1[2] * dot(w, e1)]);
  const e2 = norm3(wp);
  const e3 = cross(e1, e2);
  return [e1[0], e1[1], e1[2], e2[0], e2[1], e2[2], e3[0], e3[1], e3[2]];
}

// R such that R * F1 = F2, i.e. R = F2 * F1^T (columns as basis vectors).
function alignR(u1, w1, u2, w2) {
  const F1 = frame(u1, w1); // columns c0,c1,c2 stored as [c0x,c0y,c0z,c1x,c1y,c1z,c2x,c2y,c2z]
  const F2 = frame(u2, w2);
  // R[i][j] = sum over k of F2[i][k] * F1[j][k]
  // F1 as matrix M1 has entries M1[row][col] = F1[3*col + row]
  const M1 = (r, c) => F1[3 * c + r];
  const M2 = (r, c) => F2[3 * c + r];
  const R = new Array(9);
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      R[3 * j + i] = M2(i, 0) * M1(j, 0) + M2(i, 1) * M1(j, 1) + M2(i, 2) * M1(j, 2);
    }
  }
  return R;
}

// Multiply 3x3 (column-major flat) by 3-vector.
export function apply(R, v) {
  return [
    R[0] * v[0] + R[3] * v[1] + R[6] * v[2],
    R[1] * v[0] + R[4] * v[1] + R[7] * v[2],
    R[2] * v[0] + R[5] * v[1] + R[8] * v[2],
  ];
}

// Transpose apply: R^T * v
function applyT(R, v) {
  return [
    R[0] * v[0] + R[1] * v[1] + R[2] * v[2],
    R[3] * v[0] + R[4] * v[1] + R[5] * v[2],
    R[6] * v[0] + R[7] * v[1] + R[8] * v[2],
  ];
}

function identity() {
  return [1, 0, 0, 0, 1, 0, 0, 0, 1];
}

// Test whether R sends every vertex to some vertex (permutation on the set).
function isSymmetry(R, vertices) {
  const V = vertices.length;
  const hit = new Uint8Array(V);
  for (let i = 0; i < V; i++) {
    const p = apply(R, vertices[i]);
    let bestJ = -1;
    let bestD = Infinity;
    for (let j = 0; j < V; j++) {
      const d = (p[0] - vertices[j][0]) ** 2 + (p[1] - vertices[j][1]) ** 2 + (p[2] - vertices[j][2]) ** 2;
      if (d < bestD) {
        bestD = d;
        bestJ = j;
      }
    }
    if (bestD > EPS * EPS || bestJ < 0 || hit[bestJ]) return false;
    hit[bestJ] = 1;
  }
  return true;
}

// Compute a rotation R_j for every vertex j such that R_j * v_0 = v_j and R_j
// is a symmetry of the polyhedron. Returns array indexed by j.
export function buildSymmetry({ vertices, faces }) {
  const V = vertices.length;

  // Vertex adjacency
  const adj = Array.from({ length: V }, () => new Set());
  faces.forEach((f) => {
    for (let i = 0; i < f.length; i++) {
      adj[f[i]].add(f[(i + 1) % f.length]);
      adj[f[i]].add(f[(i - 1 + f.length) % f.length]);
    }
  });
  const neighbors = adj.map((s) => [...s].sort((a, b) => a - b));

  const rotations = new Array(V).fill(null);
  const stabilizers = new Array(V).fill(null);
  const anchorIdx = new Array(V).fill(-1);
  const visited = new Array(V).fill(false);

  for (let root = 0; root < V; root++) {
    if (visited[root]) continue;
    const v0 = vertices[root];
    if (!neighbors[root].length) {
      rotations[root] = identity();
      visited[root] = true;
      anchorIdx[root] = root;
      stabilizers[root] = [identity()];
      continue;
    }
    const n0Idx = neighbors[root][0];
    const n0 = vertices[n0Idx];

    rotations[root] = identity();
    visited[root] = true;
    anchorIdx[root] = root;

    for (let j = 0; j < V; j++) {
      if (visited[j]) continue;
      for (const nj of neighbors[j]) {
        const R = alignR(v0, n0, vertices[j], vertices[nj]);
        if (isSymmetry(R, vertices)) {
          rotations[j] = R;
          visited[j] = true;
          anchorIdx[j] = root;
          break;
        }
      }
    }

    // Stabilizer of v_root: rotations that fix v_root (n-fold axis through it).
    const stab = [identity()];
    for (const nk of neighbors[root]) {
      if (nk === n0Idx) continue;
      const R = alignR(v0, n0, v0, vertices[nk]);
      if (isSymmetry(R, vertices)) stab.push(R);
    }
    for (let j = 0; j < V; j++) {
      if (anchorIdx[j] === root) stabilizers[j] = stab;
    }
  }

  return {
    rotations,
    stabilizers,
    anchorIdx,
    // Legacy: single-orbit callers read stabilizer of vertex 0.
    stabilizer: stabilizers[0] || [identity()],
  };
}

// Apply a symmetric-drag: user moved vertex i from vertices[i] to pNew.
// Only vertices in the SAME orbit as `i` are moved so multi-orbit templates
// keep their other-orbit vertices intact.
export function symmetricDrag(vertices, rotations, stabilizer, i, pNew, anchorIdx, stabilizers) {
  const Ri = rotations[i];
  if (!Ri) return vertices.map((v, k) => (k === i ? pNew : v));
  const pCanon = applyT(Ri, pNew);

  // Per-orbit stabilizer if available, else fall back to the legacy one.
  const stab = (stabilizers && stabilizers[i] && stabilizers[i].length)
    ? stabilizers[i]
    : (stabilizer && stabilizer.length ? stabilizer : [identity()]);

  const acc = [0, 0, 0];
  for (const S of stab) {
    const q = apply(S, pCanon);
    acc[0] += q[0];
    acc[1] += q[1];
    acc[2] += q[2];
  }
  const len = Math.hypot(acc[0], acc[1], acc[2]);
  const pSym = len > 1e-9
    ? [acc[0] / len, acc[1] / len, acc[2] / len]
    : [vertices[i][0], vertices[i][1], vertices[i][2]];

  const anchor = anchorIdx ? anchorIdx[i] : null;
  return vertices.map((v, k) => {
    const Rk = rotations[k];
    if (!Rk) return v;
    if (anchor !== null && anchorIdx && anchorIdx[k] !== anchor) return v;
    return apply(Rk, pSym);
  });
}

// ---------- Partial symmetry modes ----------

const MATCH_EPS = 1e-3;

function findClosest(vertices, target) {
  let bestJ = -1;
  let bestD = Infinity;
  for (let j = 0; j < vertices.length; j++) {
    const d =
      (vertices[j][0] - target[0]) ** 2 +
      (vertices[j][1] - target[1]) ** 2 +
      (vertices[j][2] - target[2]) ** 2;
    if (d < bestD) {
      bestD = d;
      bestJ = j;
    }
  }
  return { j: bestJ, dist: Math.sqrt(bestD) };
}

// Rotation matrix around a unit axis by `angle` (Rodrigues), column-major flat.
function rotAxisAngle(ax, ay, az, angle) {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const t = 1 - c;
  return [
    t * ax * ax + c,       t * ay * ax + s * az,  t * az * ax - s * ay,
    t * ax * ay - s * az,  t * ay * ay + c,       t * az * ay + s * ax,
    t * ax * az + s * ay,  t * ay * az - s * ax,  t * az * az + c,
  ];
}

function dragAntipodal(vertices, i, pNew) {
  const antipode = [-vertices[i][0], -vertices[i][1], -vertices[i][2]];
  const { j, dist } = findClosest(vertices, antipode);
  const nv = vertices.slice();
  nv[i] = pNew;
  if (j >= 0 && j !== i && dist < MATCH_EPS) {
    nv[j] = [-pNew[0], -pNew[1], -pNew[2]];
  }
  return nv;
}

// Mirror-drag along an arbitrary axis. `axis` is 0/1/2 for X/Y/Z.
// The reflection sends each component through zero along that axis.
function dragMirrorAxis(vertices, i, pNew, axis) {
  const src = vertices[i];
  const mirror = [src[0], src[1], src[2]];
  mirror[axis] = -mirror[axis];
  const { j, dist } = findClosest(vertices, mirror);
  const nv = vertices.slice();
  if (j === i && dist < MATCH_EPS) {
    // Vertex lies on the mirror plane — snap the axis coord to 0 to keep it.
    const p = [pNew[0], pNew[1], pNew[2]];
    p[axis] = 0;
    const l = Math.hypot(p[0], p[1], p[2]) || 1;
    nv[i] = [p[0] / l, p[1] / l, p[2] / l];
    return nv;
  }
  nv[i] = pNew;
  if (j >= 0 && j !== i && dist < MATCH_EPS) {
    const mirroredNew = [pNew[0], pNew[1], pNew[2]];
    mirroredNew[axis] = -mirroredNew[axis];
    nv[j] = mirroredNew;
  }
  return nv;
}

function dragRing(vertices, sym, i, pNew) {
  const nFold = sym.stabilizer ? sym.stabilizer.length : 1;
  if (nFold <= 1) {
    // No non-trivial rotational stabilizer available (e.g. truncated icosahedron
    // vertices). Fall back to updating just the dragged vertex.
    return vertices.map((v, k) => (k === i ? pNew : v));
  }
  // Fixed rotation axis through the reference vertex 0. When the user drags
  // any vertex OFF this axis, N-1 other vertices follow via rotation around
  // the axis, producing a rotational "ring".
  const ax = vertices[0][0];
  const ay = vertices[0][1];
  const az = vertices[0][2];
  const nv = vertices.slice();
  nv[i] = pNew;
  for (let k = 1; k < nFold; k++) {
    const R = rotAxisAngle(ax, ay, az, (2 * Math.PI * k) / nFold);
    const rotVi = apply(R, vertices[i]);
    const { j, dist } = findClosest(vertices, rotVi);
    if (j >= 0 && j !== i && dist < MATCH_EPS) {
      nv[j] = apply(R, pNew);
    }
  }
  return nv;
}

// Unified drag dispatcher.
export function dragWithMode(mode, vertices, sym, i, pNew) {
  switch (mode) {
    case 'antipodal':
      return dragAntipodal(vertices, i, pNew);
    case 'mirrorX':
      return dragMirrorAxis(vertices, i, pNew, 0);
    case 'mirrorY':
    case 'mirror': // legacy alias
      return dragMirrorAxis(vertices, i, pNew, 1);
    case 'mirrorZ':
      return dragMirrorAxis(vertices, i, pNew, 2);
    case 'ring':
      return dragRing(vertices, sym, i, pNew);
    case 'full':
      return symmetricDrag(vertices, sym.rotations, sym.stabilizer, i, pNew, sym.anchorIdx, sym.stabilizers);
    case 'none':
    default:
      return vertices.map((v, k) => (k === i ? pNew : v));
  }
}
